import { useState } from 'react';

import { Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import SuspenseLoader from 'src/components/SuspenseLoader';
import TabPages from 'src/components/TabPages/TabPages';
import { useGlobal } from 'src/contexts/GlobalContext';
import { useUpdateCampaign } from 'src/hooks/api/useSiteEndpoint';
import { useAppNavigation } from 'src/routes';

import { useCampaignDetails } from './CampaignDetailsContext';
import Design from './tabPages/Design';
import Outreach from './tabPages/Outreach';
import Settings from './tabPages/Settings';

type TabKey = 'settings' | 'design' | 'outreach';

interface TabItem {
  key: TabKey;
  label: string;
  Component: React.ReactNode;
}

export const CAMPAIGN_TABS: TabItem[] = [
  {
    key: 'settings',
    label: 'site.campaigns.campaign.settings.title',
    Component: <Settings />,
  },
  {
    key: 'design',
    label: 'site.campaigns.campaign.design.title',
    Component: <Design />,
  },
  {
    key: 'outreach',
    label: 'site.campaigns.campaign.outreach.title',
    Component: <Outreach />,
  },
];

export const CampaignDetailsTab = () => {
  const navigate = useNavigate();
  const { id, campaignId, tab } = useParams<{ id: string; campaignId: string; tab: TabKey }>();
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();
  const { navigateTo } = useAppNavigation();
  const { formMethods, campaign, isLoading, site, isSiteLoading, isUpdatingCampaign, handleSave, handleCancel } =
    useCampaignDetails();
  const [isEditing, setIsEditing] = useState(false);
  const { mutate: updateCampaign } = useUpdateCampaign(id ?? '', campaignId ?? '');

  const { isDirty } = formMethods.formState;

  const handleTabChange = (key: string) => {
    navigate(`/sites/${id}/campaigns/${campaignId}/${key}`);
  };

  const items = CAMPAIGN_TABS.map((item) => ({
    key: item.key,
    label: t(item.label),
    Component: item.Component,
  }));

  const handleNavigateBack = () => {
    if (isEditing) {
      setIsEditing(false);
      return;
    }
    navigateTo.siteDetails(id, 'campaigns');
  };

  const handleUpdateName = async (name: string) => {
    if (campaign?.id && name.length && campaign?.name !== name) {
      try {
        await new Promise((resolve, reject) => {
          if (campaign?.id && site?.id) {
            const payload = {
              id: site?.id,
              campaignId: campaign?.id,
              name,
            };

            updateCampaign(payload, {
              onSuccess: () => resolve(true),
              onError: (error) => reject(error),
            });
          }
        });
        setIsEditing(false);
      } catch (error) {
        console.error('Error during name update:', error);
      }
    }
    setIsEditing(false);
  };

  const action = isDesktop && (
    <Space>
      <Button onClick={handleCancel} disabled={!isDirty || isEditing}>
        {t('site.cancel')}
      </Button>
      <Button type="primary" onClick={handleSave} disabled={!isDirty || isEditing} loading={isUpdatingCampaign}>
        {t('site.campaigns.campaign.save')}
      </Button>
    </Space>
  );

  if (isLoading || isSiteLoading) {
    return <SuspenseLoader />;
  }

  return (
    <TabPages
      tab={tab}
      tabItems={items}
      handleTabChange={handleTabChange}
      action={action}
      titleName={campaign?.name || ''}
      handleNavigateBack={handleNavigateBack}
      activeTabKey={tab}
      handleUpdateName={handleUpdateName}
      handleSave={handleSave}
      handleCancel={handleCancel}
      isDirty={isDirty}
      cancelButtonText={t('general.cancel')}
      saveButtonText={t('site.campaigns.campaign.save')}
      handleReset={handleCancel}
    />
  );
};
